import React, { useEffect, useState } from "react";
import moment from "moment";
import { Icon } from "@iconify/react";
import { enqueueSnackbar } from "notistack";
import { useLocation, useNavigate } from "react-router-dom";
import getAllBusinessChangeRequests from "../../controllers/user/getAllBusinessChangeRequests";
import CommonPaginationSection from "../../components/common/CommonPaginationSection";
import { URLdecode, URLencode } from "../../utils/url";
import CustomerVerificationReviewPanel from "../../components/modal/customerModal/CustomerVerificationReviewPanel";
import CustomerReasonDialog from "../../components/modal/customerModal/CustomerReasonDialog";
import rejectBusinessVerification from "../../controllers/user/rejectBusinessVerification";
import getAccountById from "../../controllers/user/getAccountById";

const FIELD_LABELS = {
  CACCertificateDoc: "CAC certificate",
  CACCO2Doc: "CAC status report (CO2)",
  CACC07Doc: "CAC particulars of directors (CO7)",
  identityVerificationDoc: "Director's ID",
  utilityBill: "Utility bill",
  tradingName: "Trading name",
  agencyName: "Agency name",
  businessEmail: "Business email",
  businessPhone: "Business phone",
  registeredBusinessName: "Registered business name",
  companyNumber: "RC number",
  taxIdentification: "TIN",
};

const STATUS_FILTERS = [
  { label: "All", value: "" },
  { label: "Pending", value: "Pending" },
  { label: "Approved", value: "Approved" },
  { label: "Rejected", value: "Rejected" },
];

function fieldLabel(field) {
  return (
    FIELD_LABELS[field] ||
    String(field || "Field")
      .replace(/([A-Z])/g, " $1")
      .trim()
  );
}

function statusTone(status) {
  const s = String(status || "").toLowerCase();
  if (
    s === "pending" ||
    s === "submitted" ||
    s === "in_review" ||
    s === "in-review"
  ) {
    return "bg-[#FFF8EE] text-[#7A4A08] border-[#F0D3A6]";
  }
  if (s === "approved" || s === "accepted") {
    return "bg-emerald-50 text-emerald-800 border-emerald-200";
  }
  if (s === "rejected" || s === "declined") {
    return "bg-[#FDF2F2] text-[#A81E27] border-[#F0C2C4]";
  }
  return "bg-[#F8FAFC] text-[#5A6478] border-[#E3E8F0]";
}

function requestTime(row) {
  return row?.submittedAt || row?.createdAt || row?.updatedAt;
}

function accountIdOf(row) {
  const a = row?.account;
  if (!a) return "";
  if (typeof a === "string") return a;
  return a._id || a.id || "";
}

function agencyNameOf(row) {
  const a = row?.account;
  if (!a || typeof a === "string") return null;
  return (
    a?.detail?.tradingName ||
    a?.detail?.agencyName ||
    a?.agencyName ||
    [a?.firstName, a?.lastName].filter(Boolean).join(" ") ||
    a?.email ||
    null
  );
}

/**
 * All agents' business-change requests (admin list).
 */
export default function BusinessRequestsTable() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("Pending");
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 20,
    total: 0,
  });
  const [detail, setDetail] = useState(null); // { request, account }
  const [detailLoading, setDetailLoading] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectBusy, setRejectBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    const res = await getAllBusinessChangeRequests({
      status: status || undefined,
      limit: pagination.perPage,
      offset: (pagination.page - 1) * pagination.perPage,
    });
    setLoading(false);
    if (!res?.return) {
      setRows([]);
      enqueueSnackbar(res?.msg || "Couldn't load business requests.", {
        variant: "error",
      });
      return;
    }
    const list = Array.isArray(res.data) ? [...res.data] : [];
    list.sort((a, b) => {
      const ta = new Date(requestTime(a) || 0).getTime();
      const tb = new Date(requestTime(b) || 0).getTime();
      return tb - ta;
    });
    setRows(list);
    setPagination((prev) => ({
      ...prev,
      total: res.meta?.total ?? list.length,
    }));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, pagination.page, pagination.perPage]);

  const openDetail = async (row) => {
    const accountId = accountIdOf(row);
    setDetailLoading(true);
    setDetail({ request: row, account: null });

    if (accountId) {
      if (row?.account && typeof row.account === "object") {
        setDetail({ request: row, account: row.account });
        setDetailLoading(false);
        return;
      }
      const res = await getAccountById(accountId);
      setDetail({
        request: row,
        account: res?.return ? res.data : { _id: accountId },
      });
    }
    setDetailLoading(false);
  };

  const openAgent = (row) => {
    const accountId = accountIdOf(row);
    if (!accountId) {
      enqueueSnackbar("No account linked to this request.", {
        variant: "warning",
      });
      return;
    }
    const params = URLdecode();
    navigate(
      `${location.pathname}?${URLencode({
        ...params,
        view: "agents",
        order: accountId,
      })}`,
    );
  };

  const confirmReject = async (reason) => {
    const trimmed = String(reason || "").trim();
    if (!trimmed) {
      enqueueSnackbar("Add a short reason so the agent knows what to fix.", {
        variant: "warning",
      });
      return;
    }
    const accountId = detail?.account?._id || accountIdOf(detail?.request);
    const requestId = detail?.request?._id || detail?.request?.id;
    if (!accountId || !requestId) return;

    setRejectBusy(true);
    const res = await rejectBusinessVerification(accountId, requestId, trimmed);
    setRejectBusy(false);
    if (!res?.return) {
      enqueueSnackbar(res?.msg || "Couldn't reject this request.", {
        variant: "error",
      });
      return;
    }
    enqueueSnackbar("Request rejected.", { variant: "success" });
    setRejectOpen(false);
    setDetail(null);
    load();
  };

  if (detail) {
    return (
      <div className="w-full flex flex-col pb-8 min-h-[70vh]">
        <CustomerReasonDialog
          open={rejectOpen}
          title="Reject business change request"
          description="They'll see your reason and can fix it, then request again."
          placeholder="e.g. Document is unclear — upload a clearer scan."
          confirmLabel="Reject request"
          cancelLabel="Keep pending"
          loading={rejectBusy}
          onClose={() => {
            if (!rejectBusy) setRejectOpen(false);
          }}
          onConfirm={confirmReject}
        />
        <div className="flex items-center gap-2 mb-3">
          <button
            type="button"
            onClick={() => setDetail(null)}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm font-semibold text-[#0F172A] hover:bg-[#F8FAFC]"
          >
            <Icon icon="mdi:arrow-left" width={18} height={18} />
            Back to list
          </button>
          {accountIdOf(detail.request) ? (
            <button
              type="button"
              onClick={() => openAgent(detail.request)}
              className="ml-auto inline-flex h-9 items-center rounded-lg border border-[#D6DEEA] bg-white px-3 text-sm font-semibold text-[#111827] hover:bg-[#F8FAFC]"
            >
              Open agent
            </button>
          ) : null}
        </div>
        <div className="rounded-xl border border-[#E3E8F0] bg-white overflow-hidden min-h-[60vh] flex flex-col">
          {detailLoading ? (
            <div className="flex flex-1 items-center justify-center py-16">
              <div className="primaryLoader" />
            </div>
          ) : (
            <CustomerVerificationReviewPanel
              data={detail.account || { _id: accountIdOf(detail.request) }}
              changeRequest={detail.request}
              changeRequestLoading={false}
              onReject={() => setRejectOpen(true)}
              onApproved={() => {
                setDetail(null);
                load();
              }}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col pb-32">
      <span className="text-xl font-semibold">Business requests</span>
      <p className="mt-1 text-sm text-[#6D7C94]">
        Verification and profile change requests across all agents.
      </p>

      <div className="mt-3 flex flex-wrap gap-2">
        {STATUS_FILTERS.map((f) => {
          const selected = status === f.value;
          return (
            <button
              key={f.label}
              type="button"
              onClick={() => {
                setPagination((p) => ({ ...p, page: 1 }));
                setStatus(f.value);
              }}
              className={`h-10 px-3 rounded-md text-sm font-bold ${
                selected
                  ? "text-white bg-[#4E5662]"
                  : "text-[#6D7C94] bg-[#F8F8F8]"
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="w-full flex flex-col py-10 items-center justify-center">
          <div className="primaryLoader" />
        </div>
      ) : !rows.length ? (
        <div className="mt-6 rounded-xl border border-[#E3E8F0] bg-white px-4 py-12 text-center text-sm text-[#6D7C94]">
          No business requests found
          {status ? ` with status “${status}”` : ""}.
        </div>
      ) : (
        <>
          <div className="mt-4 overflow-hidden rounded-xl border border-[#E3E8F0] bg-white">
            {rows.map((row) => {
              const id = row?._id || row?.id;
              const changes = Array.isArray(row?.changes) ? row.changes : [];
              const summary = changes
                .map((c) => fieldLabel(c?.field))
                .filter(Boolean)
                .slice(0, 3)
                .join(", ");
              const more = changes.length > 3 ? ` +${changes.length - 3}` : "";
              const when = requestTime(row);
              const agency = agencyNameOf(row);

              return (
                <div
                  key={id}
                  className="flex flex-wrap items-center gap-3 border-b border-[#EEF1F6] px-4 py-3 last:border-b-0 hover:bg-[#F8FAFC]/60"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="m-0 text-sm font-semibold text-[#0F172A]">
                        {agency || "Unknown agency"}
                      </p>
                      <span
                        className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${statusTone(
                          row?.status,
                        )}`}
                      >
                        {row?.status || "Unknown"}
                      </span>
                    </div>
                    <p className="m-0 mt-1 text-[12px] text-[#6D7C94]">
                      {row?.section ? `${row.section} · ` : ""}
                      {changes.length
                        ? `${changes.length} change${
                            changes.length === 1 ? "" : "s"
                          }${summary ? `: ${summary}${more}` : ""}`
                        : "No field changes listed"}
                      {when
                        ? ` · ${moment(when).format("DD MMM YYYY, HH:mm")}`
                        : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openAgent(row)}
                      className="inline-flex h-9 items-center rounded-lg border border-[#D6DEEA] bg-white px-3 text-sm font-semibold text-[#111827] hover:bg-[#F8FAFC]"
                    >
                      Agent
                    </button>
                    <button
                      type="button"
                      onClick={() => openDetail(row)}
                      className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-[#D6DEEA] bg-white px-3 text-sm font-semibold text-[#111827] hover:bg-[#F8FAFC]"
                    >
                      <Icon icon="mdi:eye-outline" width={16} height={16} />
                      Open detail
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <CommonPaginationSection
            perPage={pagination.perPage}
            page={pagination.page}
            total={pagination.total}
            callback={(page, perPage) =>
              setPagination({ ...pagination, page, perPage })
            }
          />
        </>
      )}
    </div>
  );
}
